// useVoiceInput.ts — React hook for voice-to-text input
//
// Manages microphone access, streams audio to Deepgram STT,
// and provides interim/final transcript text. The final transcript
// is what gets sent to Anna via useAnnaChat.sendMessage().
//
// Design: voice input produces text. The existing text pipeline
// handles everything from there. Voice never bypasses text.

import { useState, useCallback, useRef, useEffect } from 'react';
import { openDeepgramStream, type DeepgramConnection, type TranscriptEvent } from '@services/deepgramService';

export type VoiceInputState = 'idle' | 'requesting-mic' | 'listening' | 'processing' | 'error';

interface UseVoiceInputOptions {
  /** BCP-47 language code. Default: 'da' (Danish) */
  language?: string;
  /** Called when a final transcript is ready to send */
  onFinalTranscript?: (text: string) => void;
  /** Auto-send final transcripts (vs manual confirm). Default: true */
  autoSend?: boolean;
}

interface UseVoiceInputReturn {
  state: VoiceInputState;
  /** Current interim transcript (updates in real-time as user speaks) */
  interimTranscript: string;
  /** Accumulated final transcript (for manual-send mode) */
  finalTranscript: string;
  /** Error message if state is 'error' */
  error: string | null;
  /** Whether the mic is currently active */
  isListening: boolean;
  /** Start listening — requests mic permission if needed */
  startListening: () => Promise<void>;
  /** Stop listening and finalize */
  stopListening: () => void;
  /** Clear accumulated transcript (manual-send mode) */
  clearTranscript: () => void;
  /** Raw audio level 0-1 for visualizer */
  audioLevel: number;
}

export function useVoiceInput(options: UseVoiceInputOptions = {}): UseVoiceInputReturn {
  const { language = 'da', onFinalTranscript, autoSend = true } = options;

  const [state, setState] = useState<VoiceInputState>('idle');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const connectionRef = useRef<DeepgramConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (connectionRef.current) {
      connectionRef.current.close();
      connectionRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => cleanup, [cleanup]);

  const startListening = useCallback(async () => {
    if (state === 'listening') return;

    setError(null);
    setState('requesting-mic');

    try {
      // 1. Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;

      // 2. Set up audio processing
      const audioContext = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);

      // Analyser for visual audio level
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      analyserRef.current = analyser;
      source.connect(analyser);

      // Audio level animation loop
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(Math.min(1, avg / 128));
        animFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();

      // ScriptProcessor to capture raw PCM for Deepgram
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      source.connect(processor);
      processor.connect(audioContext.destination);

      // 3. Open Deepgram connection
      setState('processing');

      const connection = await openDeepgramStream(
        language,
        (event: TranscriptEvent) => {
          if (event.type === 'interim') {
            setInterimTranscript(event.text);
          } else if (event.type === 'final' && event.text.trim()) {
            setInterimTranscript('');
            if (autoSend && onFinalTranscript) {
              onFinalTranscript(event.text.trim());
            } else {
              setFinalTranscript(prev => {
                const combined = prev ? `${prev} ${event.text.trim()}` : event.text.trim();
                return combined;
              });
            }
          }
        },
        (errMsg: string) => {
          setError(errMsg);
          setState('error');
          cleanup();
        },
      );
      connectionRef.current = connection;

      // 4. Start sending audio data to Deepgram
      processor.onaudioprocess = (e: AudioProcessingEvent) => {
        if (!connectionRef.current?.isOpen()) return;
        const inputData = e.inputBuffer.getChannelData(0);
        // Convert Float32 to Int16
        const int16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        connectionRef.current.send(int16.buffer);
      };

      setState('listening');
    } catch (err) {
      cleanup();
      const message = err instanceof Error ? err.message : 'Microphone access failed';
      if (message.includes('Permission denied') || message.includes('NotAllowedError')) {
        setError('mic_permission_denied');
      } else {
        setError(message);
      }
      setState('error');
    }
  }, [state, language, autoSend, onFinalTranscript, cleanup]);

  const stopListening = useCallback(() => {
    cleanup();
    setInterimTranscript('');
    setAudioLevel(0);
    setState('idle');
  }, [cleanup]);

  const clearTranscript = useCallback(() => {
    setFinalTranscript('');
    setInterimTranscript('');
  }, []);

  return {
    state,
    interimTranscript,
    finalTranscript,
    error,
    isListening: state === 'listening',
    startListening,
    stopListening,
    clearTranscript,
    audioLevel,
  };
}
